import type { HelpAgentDraftAttachment, HelpAgentSubmitHandler } from '@/types/attachments';
import type { ChatConfig, ChatImage, ServerMessage } from '@/types/chat';
import type { HelpAgentStreamHandler } from '@/types/public';
import { createId } from '@/utils/ids';

export interface HelpAgentMockBackendOptions {
  responseDelayMs?: number;
}

export interface HelpAgentMockBackend {
  submitHandler: HelpAgentSubmitHandler;
  streamHandler: HelpAgentStreamHandler;
}

interface QueuedRequest {
  config: ChatConfig;
  question: string;
  attachments: readonly HelpAgentDraftAttachment[];
}

function buildMockResponseContent(
  question: string,
  attachments: readonly HelpAgentDraftAttachment[],
): string {
  const trimmedQuestion = question.trim();

  if (trimmedQuestion) {
    return [
      `Mock response for: ${trimmedQuestion}`,
      '',
      'This message is served by the local help-agent mock backend.',
      attachments.length > 0
        ? `Attached files: ${attachments.map((attachment) => attachment.name).join(', ')}`
        : 'No files were attached.',
    ].join('\n');
  }

  if (attachments.length > 0) {
    return `Mock response for ${attachments.length} attached file${attachments.length === 1 ? '' : 's'}.`;
  }

  return 'Mock response';
}

function buildMockResponseImages(
  attachments: readonly HelpAgentDraftAttachment[],
): ChatImage[] | undefined {
  const imageAttachments = attachments.filter(
    (attachment) => attachment.kind === 'image' && Boolean(attachment.previewUrl),
  );

  if (imageAttachments.length === 0) {
    return undefined;
  }

  return imageAttachments.map((attachment, index) => ({
    url: attachment.previewUrl || '',
    caption: attachment.name,
    step_number: index + 1,
  }));
}

function buildMockServerMessage(entityId: string, queuedRequest: QueuedRequest): ServerMessage {
  return {
    entity_id: entityId,
    request: queuedRequest.question,
    response: {
      content: buildMockResponseContent(queuedRequest.question, queuedRequest.attachments),
      images: buildMockResponseImages(queuedRequest.attachments),
    },
    created_at: new Date().toISOString(),
  };
}

export function isHelpAgentMockModeEnabled(locationSearch: string = window.location.search): boolean {
  const params = new URLSearchParams(locationSearch);
  const rawValue = (params.get('helpWidgetMock') || params.get('mock') || '').trim().toLowerCase();
  return rawValue === '1' || rawValue === 'true' || rawValue === 'yes' || rawValue === 'on';
}

export function createHelpAgentMockBackend(
  options: HelpAgentMockBackendOptions = {},
): HelpAgentMockBackend {
  const responseDelayMs = options.responseDelayMs ?? 80;
  const queuedRequests = new Map<string, QueuedRequest>();

  return {
    submitHandler: async (request) => {
      const entityId = createId('mock');
      queuedRequests.set(entityId, {
        config: request.config,
        question: request.question,
        attachments: request.attachments,
      });

      return {
        entityId,
        sessionId: request.config.sessionId || null,
      };
    },

    streamHandler: (request) => {
      const queuedRequest = queuedRequests.get(request.entityId) || {
        config: request.config,
        question: '',
        attachments: [],
      };

      request.onStatusChange?.('connecting');

      let disposed = false;
      const connectedTimer = globalThis.setTimeout(() => {
        if (disposed) {
          return;
        }

        request.onStatusChange?.('connected');
      }, 0);

      const messageTimer = globalThis.setTimeout(() => {
        if (disposed) {
          return;
        }

        request.onMessage(buildMockServerMessage(request.entityId, queuedRequest));
        queuedRequests.delete(request.entityId);
      }, responseDelayMs);

      return () => {
        disposed = true;
        globalThis.clearTimeout(connectedTimer);
        globalThis.clearTimeout(messageTimer);
        queuedRequests.delete(request.entityId);
        request.onStatusChange?.('disconnected');
      };
    },
  };
}
